// src/component/AdminSekaimuraTokenRegister.js
// JSXあり / JS（TSなし）
// 依存: react, @mui/material, firebase v9

import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  TextField,
  Button,
  Divider,
  Alert,
  LinearProgress,
  Chip,
} from "@mui/material";

import { collection, doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../api/firebase"; // ←あなたのプロジェクトで auth export が無いなら getAuth() に変更してOK
import { AppThemeContext } from "./AppShell";

function normalizeUidLines(raw = "") {
  // 改行（\n, \r\n）やタブ混入を許容
  const lines = String(raw)
    .replace(/\t/g, "\n")
    .split(/\r?\n/g)
    .map((s) => s.trim())
    .filter(Boolean);

  // 入力内重複を除去（順序は維持）
  const seen = new Set();
  const uniq = [];
  for (const uid of lines) {
    if (!seen.has(uid)) {
      seen.add(uid);
      uniq.push(uid);
    }
  }
  return { lines, uniq };
}

// ブラウザ標準 crypto で base64url 風のトークン生成
// function generateToken(lenBytes = 18) {
//   const bytes = new Uint8Array(lenBytes);
//   crypto.getRandomValues(bytes);
//   // base64url
//   const b64 = btoa(String.fromCharCode(...bytes))
//     .replace(/\+/g, "-")
//     .replace(/\//g, "_")
//     .replace(/=+$/g, "");
//   return b64;
// }

// 同時実行数を制限（Firestoreの連打を避ける）
async function mapWithConcurrency(items, concurrency, mapper) {
  const results = new Array(items.length);
  let index = 0;

  const workers = new Array(Math.min(concurrency, items.length))
    .fill(0)
    .map(async () => {
      while (index < items.length) {
        const i = index++;
        results[i] = await mapper(items[i], i);
      }
    });

  await Promise.all(workers);
  return results;
}

export default function AdminSekaimuraTokenRegister() {
  const theme = React.useContext(AppThemeContext) || {};
  const accent = theme.railActive ?? "#5A35FF";

  const [rawUids, setRawUids] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null); // { inputCount, uniqueCount, duplicatesInInput, existedCount, createdCount, errorsCount }

  const parsed = useMemo(() => normalizeUidLines(rawUids), [rawUids]);
  const duplicatesInInput = parsed.lines.length - parsed.uniq.length;

  const canRun = parsed.uniq.length > 0 && !running;

  const handleRun = async () => {
    if (!canRun) return;

    setRunning(true);
    setResult(null);

    const uidList = parsed.uniq;

    try {
      const currentUid = auth?.currentUser?.uid; // auth未exportなら getAuth().currentUser.uid に差し替え
      if (!currentUid) {
        throw new Error("認証情報が取得できません（currentUser が null）");
      }

      let existedCount = 0;
      let createdCount = 0;
      let errorsCount = 0;

      // 1) 既存確認 → 2) 未登録のみ setDoc
      // docId を uid にする運用を前提（既存スキップが簡単・高速）
      await mapWithConcurrency(uidList, 8, async (uid) => {
        try {
          const ref = doc(db, "sekaimuraUserToken", uid);
          const snap = await getDoc(ref);

          if (snap.exists()) {
            existedCount += 1;
            return;
          }

        //   const token = generateToken();

          await setDoc(ref, {
            uid, // あると管理しやすい
            token: uid,
            registeredAt: serverTimestamp(),
            registeredBy: currentUid,
            updatedAt: serverTimestamp(),
            updatedBy: currentUid,
          });

          createdCount += 1;
        } catch (e) {
          console.error("register error:", uid, e);
          errorsCount += 1;
        }
      });

      setResult({
        inputCount: parsed.lines.length,
        uniqueCount: uidList.length,
        duplicatesInInput,
        existedCount,
        createdCount,
        errorsCount,
      });
    } catch (e) {
      console.error(e);
      setResult({
        errorMessage: e?.message || String(e),
      });
    } finally {
      setRunning(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
      <Box sx={{ mb: 2.2 }}>
        <Typography
          variant="overline"
          sx={{ letterSpacing: "0.18em", fontSize: 11, opacity: 0.8 }}
        >
          ADMIN
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: "0.02em" }}>
          セカイムラUID登録
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.6, color: "rgba(0,0,0,0.6)", lineHeight: 1.8 }}>
          改行区切りでUIDを貼り付けてください。既に{" "}
          <code>sekaimuraUserToken</code> に存在するUIDはスキップし、
          未登録分だけ新規作成します。
        </Typography>
      </Box>

      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.bottomBorder ?? "rgba(0,0,0,0.08)"}`,
          bgcolor: theme.surfaceBg ?? "#fff",
          boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 2.4 } }}>
          <Stack spacing={1.4}>
            <TextField
              label="UID（改行区切り）"
              placeholder={"uida\nuidb\nuidc\n..."}
              value={rawUids}
              onChange={(e) => setRawUids(e.target.value)}
              multiline
              minRows={8}
              fullWidth
              disabled={running}
            />

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip size="small" label={`入力行: ${parsed.lines.length}`} />
              <Chip size="small" label={`ユニーク: ${parsed.uniq.length}`} />
              <Chip
                size="small"
                variant={duplicatesInInput > 0 ? "filled" : "outlined"}
                sx={duplicatesInInput > 0 ? { bgcolor: `${accent}22`, color: "rgba(0,0,0,0.75)" } : {}}
                label={`入力内重複: ${duplicatesInInput}`}
              />
            </Stack>

            <Divider />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
              <Button
                variant="contained"
                onClick={handleRun}
                disabled={!canRun}
                sx={{
                  flex: 1,
                  bgcolor: accent,
                  fontWeight: 800,
                  borderRadius: 999,
                  textTransform: "none",
                  py: 1.0,
                  "&:hover": { bgcolor: accent },
                }}
              >
                登録を実行
              </Button>

              <Button
                variant="outlined"
                disabled={running}
                onClick={() => {
                  setRawUids("");
                  setResult(null);
                }}
                sx={{
                  flex: 0.6,
                  borderRadius: 999,
                  textTransform: "none",
                  py: 1.0,
                }}
              >
                クリア
              </Button>
            </Stack>

            {running && (
              <Box sx={{ mt: 0.5 }}>
                <LinearProgress />
                <Typography variant="caption" sx={{ display: "block", mt: 0.8, opacity: 0.75 }}>
                  登録中です（既存チェック → 未登録のみ追加）...
                </Typography>
              </Box>
            )}

            {!!result?.errorMessage && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {result.errorMessage}
              </Alert>
            )}

            {!!result && !result.errorMessage && (
              <Alert
                severity={result.errorsCount > 0 ? "warning" : "success"}
                sx={{ mt: 1 }}
              >
                入力 {result.inputCount} 行（ユニーク {result.uniqueCount} / 入力内重複 {result.duplicatesInInput}）
                <br />
                既存スキップ {result.existedCount} 件 / 新規追加 {result.createdCount} 件
                {result.errorsCount > 0 ? (
                  <>
                    <br />
                    エラー {result.errorsCount} 件（コンソールを確認）
                  </>
                ) : null}
              </Alert>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
